import React, {useCallback, useContext, useEffect, useState} from "react";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {PresenzeGiustificativo} from "@commons/mapping/PresenzeGiustificativo";
import {CoppiaTimbrature} from "@commons/mapping/CoppiaTimbrature";
import {TurnoDistribuzioneRecord} from "@commons/mapping/TurnoDistribuzioneRecord";
import {TurnoRecord} from "@commons/mapping/TurnoRecord";
import {FormProvider, useForm, useWatch} from "react-hook-form";
import {MemoizedBannerDay} from "../pianificazione/comps/BannerDay";
import {ModalPanelForm} from "@components/Modal2023";
import {ComboBoxTurno} from "@components/comboBox/ComboBoxTurni";
import ButtonsTimbrature from "./comps/ButtonsTimbrature";
import TimbraturePLUS from "./comps/TimbraturePLUS";
import {cloneDeep, omit} from "lodash";
import {FormUtils} from "@utilities/FormUtils";
import {Timbrature} from "@commons/services/presenze_calcolo/sub_calcolo/Timbrature";
import {PresenzeContext, UpdaterLavoratore, UpdaterRetVal} from "@commons/hooks/usePresenze";
import RiepilogoOre from "../comps/RiepilogoOre";
import {PresenzeGiustificativoEx} from "../localMapping/PresenzeGiustificativoEx";
import PulsantieraGiornataPlusSTD from "./comps/PulsantieraGiornataPlusSTD";
import {GestionePlus} from "@commons/services/presenze_calcolo/GestionePlus";
import {BannersAnomalieWarnings} from "./comps/BannersAnomalieWarnings";
import OpzioniGiornata from "./comps/OpzioniGiornata";
import LogGiornata from "./comps/LogGiornata";
import TabsRiepilogo, {TabId} from "./tabsRiepilogo";
import {EditGiustificativiHH, EditGiustificativiQI} from "../comps/EditGiustificativiPannelloGPLUS";
import {form2PresenzeGiornata, initTimbrature, PresenzeFormValues, setupFormValues, useFormData} from "./FormDataUpdater";
import AccumuliDiControllo from "../comps/AccumuliDiControllo";
import {GestionePlusData} from "@commons/services/presenze_calcolo/GestionePlusData";
import {FPNetDate} from "@commons/types";
import {useImmer} from "use-immer";
import {UserCRUDRecord} from "@commons/mapping/UserCRUDRecord";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectUser} from "@commons/core.slice";
import {Calcolo} from "@commons/services/presenze_calcolo/Calcolo";
import Calendar from "./comps/Calendar";
import {PresenzeCausaleWFRecord} from "@commons/mapping/PresenzeCausaleWF";

interface PannelloGiornataPLUSProps {
    lavoratore: LavoratorePresenzeRecord,
    updLavoratore: (lav: (LavoratorePresenzeRecord | null), updFn?: UpdaterLavoratore) => void,
    dtGiornata: FPNetDate
    onCancelEdit: () => void,
}

const mergeDay = (day2Update: PresenzeGiornataPlusRecord, payload: PresenzeFormValues, defaultValues: PresenzeFormValues) => {
    // N.B. Anche se payload contiene l'intera struttura rappresentante la giornata, saranno considerate utili solo le info direttamente associate alla form!
    // Le info "calcolate" (ad esempio, il totale delle ore lavorate) e/o "derivate" (es. maskEffettive) sono prelevate direttamente da <day> (clone di dayOriginale) e non da <payload>.
    day2Update.anomalia = payload.anomalia;
    day2Update.giornataBloccata = payload.giornataBloccata;

    day2Update.warning = defaultValues.warning;

    day2Update.warningChecked = defaultValues.warningChecked;
    day2Update.maskEffettive = defaultValues.maskEffettive;

    day2Update.effettiveManualiFlat = defaultValues.effettiveManualiFlat;
    day2Update.previsteLocked = defaultValues.previsteLocked;
    day2Update.arrotondateLocked = defaultValues.arrotondateLocked;
    day2Update.previste = Timbrature.defragStrisciata(payload.previste, false);
    day2Update.effettive = Timbrature.defragStrisciata(payload.effettive, false);
    day2Update.effettiveOriginali = Timbrature.defragStrisciata(payload.effettiveOriginali, false);
    day2Update.arrotondate = Timbrature.defragStrisciata(payload.arrotondate, false);

    day2Update.qualificaEccNC = defaultValues.qualificaEccNC;

    // Normalizzo codGiu ed eseguo defrag su giustificativi
    day2Update.causaliHH = [];
    payload.giustificativiHHGiornata.forEach(giustificativo => {
        giustificativo && day2Update.causaliHH.push(PresenzeGiustificativo.factory(omit(giustificativo, 'giustificativo')))
    });
    day2Update.causaliHH = PresenzeGiornataPlusRecord.defrag(day2Update.causaliHH);

    // Normalizzo codGiu ed eseguo defrag su giustificativi
    day2Update.causaliQI = [];
    payload.giustificativiQIGiornata.forEach(giustificativo => {
        giustificativo && day2Update.causaliQI.push(PresenzeGiustificativo.factory(omit(giustificativo, 'giustificativo')))
    });
    day2Update.causaliQI = PresenzeGiornataPlusRecord.defrag(day2Update.causaliQI);

    // Turno
    day2Update.codTurno = payload.codTurno;
    if (day2Update.codTurno === day2Update.codTurnoSviluppo) {
        day2Update.idGiorno = day2Update.idGiornoSviluppo;
    } else {
        day2Update.idGiorno = 1;
    }

    day2Update.durataPausa = defaultValues.durataPausa;
    day2Update.pausaFlex = defaultValues.pausaFlex;
    day2Update.totali = defaultValues.totali;
    day2Update.pausaInTolleranza = defaultValues.pausaInTolleranza;

}

const PannelloGiornataPLUS: React.FC<PannelloGiornataPLUSProps> = ({
                                                                       lavoratore,
                                                                       updLavoratore, // updater di lavoratore
                                                                       dtGiornata,
                                                                       onCancelEdit,
                                                                   }) => {

    const user: UserCRUDRecord | null = useAppSelector(selectUser); // User corrente
    const isEmployee = user?.userClass === 'EMPLOYEE';

    /*
        const sitePreferences = useAppSelector(selectSitePreferences);
        const mostra5Coppia: boolean | undefined = sitePreferences.presenzeOptions && sitePreferences.presenzeOptions.mostra5Coppia;
    */
    const {
        listaGiustificativi,
        listaTerminali,
        consolidatedData,
        listaUnitaLocali,
        calendarioChiusure,
        periodo,
        calcolaGiornata,
        compensaGiornata,
        soloMeseRif: soloMeseRifContext,
        setSoloMeseRif: setSoloMeseRifContext,
        ...gestione
    } = useContext(PresenzeContext);
    const [soloMeseRif, setSoloMeseRif] = useState<boolean>(soloMeseRifContext);
    const findDay = useCallback((day: PresenzeGiornataPlusRecord) => day.dtGiornata === dtGiornata, [dtGiornata]);

    // Copia locale di lavoratore e day in editing
    const [lavoratoreLocale, updateLavoratoreLocale] = useImmer<LavoratorePresenzeRecord>(cloneDeep(lavoratore));
    const {defaultValues, updateDraft, allineaDefaultValues, allineaCausali} = useFormData(lavoratoreLocale.days?.find(findDay)!, listaGiustificativi);

    const showRiepilogoAccumuli = !!(lavoratoreLocale.totaliMese && lavoratoreLocale.totaliMese.accumuliCHK.length > 0);

    const [editEffettiveOriginali, setEditEffettiveOriginali] = useState<boolean>(false);
    const [riordinoEffettive, setRiordinoEffettive] = useState<boolean>(false);
    const [calcolato, setCalcolato] = useState<boolean>(false);
    // Cicli di aggiornamento: è un banale contatore che permette di rilevare l'azione di calcolo della giornata,
    // in modo da permettere il consolidamento dei dati nel caso non si effettuino modifiche al form ma ci si limiti a calcolare...
    // Il consolidamento infatti, prevede o una modifica di almeno un campo del form o il calcolo della giornata updCycle>1
    const [updCycle, setUpdCycle] = useState<number>(0);

    const [activeTab, setActiveTab] = useState<TabId>('day');

    const {elencoTurni: turni} = consolidatedData!;

    const hook = useForm<PresenzeFormValues>({
        defaultValues, mode: 'onChange', reValidateMode: 'onChange'
    });
    const {
        reset,
        getValues,
        setValue,
        watch,
        control,
        handleSubmit,
        formState: {dirtyFields,},
    } = hook;

    /**
     * Allineo form con defaultValues a seguito di operazione di calcolo ma anche semplicemente per la modifica delle timbrature (diretta e indiretta attraverso i buttons)...
     * N.B. Non agisce invece quando vengono modificati i giustificativi orari e a quantità!
     * Al setup del componente viene comunque sempre eseguito!
     */
    useEffect(() => {
        reset(setupFormValues(defaultValues, listaGiustificativi));
        setUpdCycle(x => {
            console.log('reset(setupFormValues(defaultValues...', x + 1);
            return x + 1;
        });
    }, [defaultValues, listaGiustificativi, reset]);

    /*
        const traceManager = useMemo<TraceManager>(() => {
            return {
                getDeleteInfo: (_trace: PresenzeGiornataPlusRecord) => {
                    return undefined;
                }
            }
        }, []);
    */
    const descrizioneUL = (gestione.idAzienda === gestione.idAziendaMaster) ? GestionePlus.getDescrizioneUL(listaUnitaLocali, defaultValues.idAzienda) : undefined;

    const previsteLocked = watch('previsteLocked');
    const arrotondateLocked = watch('arrotondateLocked');
    const timbEffettive = useWatch({
        name: 'effettive',
        control
    });

    const timbEffettiveOrig = useWatch({
        name: 'effettiveOriginali',
        control
    });

    /**
     * Routine chiamata in uscita del form con consolidamento della giornata (commit dei dati)
     * @param payload
     * @param dirtyValues
     * @param closePanel se true, chiude il pannello al consolidamento del form
     */
    const consolidaGiornata = (payload: PresenzeFormValues,
                               dirtyValues: Partial<PresenzeGiornataPlusRecord>,
                               closePanel: boolean = true) => {
        // Allineo <soloMeseRif> del context con quello locale
        setSoloMeseRifContext(soloMeseRif);

        if (updCycle > 1 // Almeno un calcolo!
            || Object.entries(dirtyValues).length > 0 // Almeno un campo modificato!
        ) {
            updLavoratore(lavoratore, (target: LavoratorePresenzeRecord): UpdaterRetVal => {
                target.days = cloneDeep(lavoratoreLocale.days);
                const day2Update = target.days?.find(findDay)!;
                mergeDay(day2Update, payload, defaultValues);
                day2Update.modified = true;
                target.weeks = cloneDeep(lavoratoreLocale.weeks);
                GestionePlusData.normalizzaDays({days: target.days, weeks: target.weeks});
                target.totaliMese = cloneDeep(lavoratoreLocale.totaliMese);

                //target.causaliWF = cloneDeep(lavoratoreLocale.causaliWF);
                target.causaliWFElaborate = cloneDeep(lavoratoreLocale.causaliWFElaborate);
                target.giustificativiAttestatiElaborati = cloneDeep(lavoratoreLocale.giustificativiAttestatiElaborati);

                // Aggiorno totali, utile quando vengono fatte modifiche senza ricalcolare la giornata!
                Calcolo.updateTotaliMese(target, target.totaliMese!, soloMeseRif, gestione.cachePresenze!);
                return {} as UpdaterRetVal;
            });
        }
        if (closePanel)
            onCancelEdit();
    }

    // Effetto per ricalcolare i totali mensili relativi alle sole ore al variare di <soloMeseRif>
    useEffect(() => {
        updateLavoratoreLocale(target => {
            Calcolo.updateTotaliMese(target, target.totaliMese!, soloMeseRif, gestione.cachePresenze!);
        });
    }, [gestione.cachePresenze, soloMeseRif, updateLavoratoreLocale]);

    const isEffettiveOriginali = (effettive: CoppiaTimbrature[], effettiveOrig: CoppiaTimbrature[], maskEffettive: number): boolean => {
        if (maskEffettive !== 65535)
            return false;
        return CoppiaTimbrature.strisciateAreEquals(effettive, effettiveOrig);
    }

    const togglePrevisto = (): void => {
        const nextPrevisteLocked = !previsteLocked;
        const day = lavoratoreLocale.days?.find(findDay)!;
        let codTurno: number;
        if (!nextPrevisteLocked) {
            // Se gestione a consuntivo forzo il turno base
            codTurno = day.codTurno === -1 ? turni[0].codTurno : day.codTurno;
        } else
            codTurno = turni[0].codTurno;
        setNewPrevisto(codTurno, nextPrevisteLocked);
    }

    const cambioTurno = (e: TurnoRecord | null) => {
        if (e && e.codTurno) {
            allineaCausali(getValues());
            setNewPrevisto(e.codTurno, false);
        }
    }

    const setNewPrevisto = (codTurno: number, previsteLocked: boolean): void => {

        if (codTurno === -1) // Se gestione a consuntivo, selezionando il previsto a consuntivo blocco le previste
            previsteLocked = true;

        const turno = turni.find((t) => t.codTurno === codTurno);
        if (turno) {
            const distribuzione: TurnoDistribuzioneRecord = TurnoRecord.getDistribuzione(
                turno,
                defaultValues.idGiornoSviluppo,
                TurnoRecord.getDistribuzione(turni[0], defaultValues.idGiornoSviluppo) // Distribuzione turno base da utilizzare in casi particolari (es. Smart working "Pizzato")
            );

            updateDraft(draft => {
                draft.previsteLocked = previsteLocked;
                GestionePlus.allineaPrevisto(draft, turno, !previsteLocked, turni, distribuzione);
                PresenzeGiornataPlusRecord.resetAnomalia(draft);
            })
        }
    }

    const confirmEditEffettive = () => {
        // Allineo i flags in <maskEffettive>
        let maskEffettive = defaultValues.maskEffettive;
        timbEffettiveOrig.forEach((coppia, index) => {
            if (!CoppiaTimbrature.isNull(coppia)) {
                const pos = index * 2;
                if (coppia.utilizzaEntrata)
                    maskEffettive = maskEffettive | (1 << pos);
                else
                    maskEffettive = maskEffettive & ~(1 << pos);
                if (coppia.utilizzaUscita)
                    maskEffettive = maskEffettive | (1 << (pos + 1));
                else
                    maskEffettive = maskEffettive & ~(1 << (pos + 1))
            }
        });

        updateDraft(draft => {
            if (!CoppiaTimbrature.strisciateAreEquals(draft.effettiveOriginali, timbEffettiveOrig)) {
                PresenzeGiornataPlusRecord.resetAnomalia(draft);
            }
            if (maskEffettive !== draft.maskEffettive) {
                PresenzeGiornataPlusRecord.resetAnomalia(draft);
                if (draft.effettiveManualiFlat) {
                    for (let ind0 = 0; ind0 < draft.effettiveManualiFlat.length; ++ind0) {
                        draft.effettiveManualiFlat[ind0] = null;
                    }
                }
                draft.maskEffettive = maskEffettive;
            }

            draft.effettiveOriginali = cloneDeep(timbEffettiveOrig);
            GestionePlus.setEditEffettive(draft);
        });

        setRiordinoEffettive(false);
    }

    const cancelEditEffettive = () => {
        setRiordinoEffettive(false);
    }

    const submitHandler = useCallback((v: PresenzeFormValues, compensa: boolean) => {
        setCalcolato(true);
        const {giustificativiHHGiornata, giustificativiQIGiornata, ...day2clone} = defaultValues;
        // Clono day in modo da aver un oggetto legacy da passare alle routine di calcolo
        const day = cloneDeep<PresenzeGiornataPlusRecord>(day2clone);
        form2PresenzeGiornata(day, v);
        const result: GestionePlusData | null = compensa ? compensaGiornata(day, soloMeseRif) : calcolaGiornata(day, soloMeseRif);
        if (result) {
            updateLavoratoreLocale(target => {
                target.days = cloneDeep(result.days);
                target.weeks = cloneDeep(result.weeks);
                GestionePlusData.normalizzaDays({days: target.days, weeks: target.weeks});
                target.totaliMese = cloneDeep(result.totaliMese);

                //target.causaliWF = cloneDeep(result.causaliWF);
                target.causaliWFElaborate = cloneDeep(result.causaliWFElaborate);
                target.giustificativiAttestatiElaborati = cloneDeep(result.giustificativiAttestatiElaborati);
            });

            const day = result.days!.find(findDay)!;
            // N.B. per aggiornare l'intero oggetto, passo direttamente alla funzione updater il nuovo oggetto!
            updateDraft(setupFormValues(day, listaGiustificativi));
        }
    }, [calcolaGiornata, compensaGiornata, defaultValues, findDay, listaGiustificativi, updateDraft, updateLavoratoreLocale, soloMeseRif]);

    const calcola = useCallback(() => {
        // todo valutare come procedere in caso di form non valido
        const submitErrorHandler = (_errors: any) => {
            const v = getValues();
            // Attenzione che allineaDefaultValues resetta anche le info su i campi in errore, eliminando così l'evidenza dei campi stessi in errore
            allineaDefaultValues(v, FormUtils.getDirtyFields(dirtyFields, v) as Partial<PresenzeFormValues>);
        }
        // Calcolo sia con form valido sia con form NON VALIDO
        handleSubmit(v => submitHandler(v, false), fe => submitHandler(getValues(), false))();
    }, [allineaDefaultValues, dirtyFields, getValues, handleSubmit, submitHandler]);

    const compensa = useCallback(() => {
        // todo valutare come procedere in caso di form non valido
        const submitErrorHandler = (_errors: any) => {
            const v = getValues();
            // Attenzione che allineaDefaultValues resetta anche le info su i campi in errore, eliminando così l'evidenza dei campi stessi in errore
            allineaDefaultValues(v, FormUtils.getDirtyFields(dirtyFields, v) as Partial<PresenzeFormValues>);
        }
        // Calcolo sia con form valido sia con form NON VALIDO
        handleSubmit(v => submitHandler(v, true), fe => submitHandler(getValues(), false))();

    }, [allineaDefaultValues, dirtyFields, getValues, handleSubmit, submitHandler]);

    const onChangeOraInizio = (hhmm: string, index: number | null) => {
        if (hhmm !== null && hhmm !== undefined && (hhmm.length === 0 || hhmm.length === 4)) {
            updateDraft(draft => {
                if (draft.effettiveManualiFlat && index) {
                    draft.effettiveManualiFlat[index] = hhmm;
                    GestionePlus.setEditEffettive(draft);
                }
            })
        }
    };
    const onChangeOraFine = onChangeOraInizio;

    //console.log(isEffettiveOriginali(defaultValues.effettive, defaultValues.effettiveOriginali, defaultValues.maskEffettive));

    // N.B. è sempre aperto! Il componente è istanziato solo se la giornata a cui si riferisce è quella corrente,
    // vedi DayCellDisclosure -> Disclosure.Button onClick...

    const anomalia = watch('anomalia');
    const resetLocalAnomalia = useCallback((_causali: PresenzeGiustificativoEx[]) => {
        /*
            updateDefaultValues(draft => {
                PresenzeGiornataPlusRecord.resetAnomalia(draft);
            });
        */
        setValue('anomalia', -3, {shouldDirty: true});
        setValue('giornataBloccata', false, {shouldDirty: true});
    }, [setValue]);

    const onKeyDown = useCallback((e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'F') {
            if (editEffettiveOriginali) {
                if (!riordinoEffettive)
                    setRiordinoEffettive(true);
                else
                    setEditEffettiveOriginali(false);
            } else {
                setEditEffettiveOriginali(true);
                setRiordinoEffettive(true);
            }
        } else if (e.code === 'F1' || e.code === 'F2') {
            e.preventDefault();
            e.stopPropagation();
            calcola()

        } else if (e.code === 'F7' || e.code === 'F8') {
            e.preventDefault();
            e.stopPropagation();
            compensa()
        }
    }, [calcola, compensa, editEffettiveOriginali, riordinoEffettive]);

    useEffect(() => {
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
        }
    }, [onKeyDown]);

    const tutteLeRichiesteNonElaborate = useCallback((causaleWF: PresenzeCausaleWFRecord) => {
        return !causaleWF.elaborato && (causaleWF.giornoDt === dtGiornata)
            && lavoratoreLocale.causaliWFElaborate.findIndex(key => key === PresenzeCausaleWFRecord.getKey(causaleWF)) === -1;
    }, [dtGiornata, lavoratoreLocale.causaliWFElaborate]);

    return <ModalPanelForm isOpen={true} isChanged={() => calcolato} formControl={control} handleDismiss={onCancelEdit}
                           header={<div className="lg:max-w-[1150px] xl:max-w-[1198px] 2xl:max-w-[1548px]">
                               <MemoizedBannerDay day={defaultValues}
                                                  lavoratore={lavoratoreLocale}
                                                  giustificativi={listaGiustificativi}
                                                  turni={turni}
                                                  page='Presenze'
                                                  descrizioneUL={descrizioneUL}
                                                  anomalia={anomalia}
                               /></div>}>

        <div className="lg:max-w-[1100px] xl:max-w-[1148px] 2xl:max-w-[1498px]">
            <Calendar mese={lavoratore.mese} dtGiornata={dtGiornata} days={lavoratoreLocale.days}
                      onClickDay={
                          handleSubmit(v => {
                              consolidaGiornata(v, FormUtils.getDirtyFields(dirtyFields, v) as Partial<PresenzeFormValues>, false);
                          })}/>

            <FormProvider {...hook}>
                <form className="p-2 bg-white shadow-md rounded select-none text-[13px]"
                      onSubmit={handleSubmit(v => {
                          consolidaGiornata(v, FormUtils.getDirtyFields(dirtyFields, v) as Partial<PresenzeFormValues>);
                      }, () => alert('Form non valido!!!'))}>

                    {/* BANNERS ANOMALIE \ WARNINGS GIORNATA */}
                    <BannersAnomalieWarnings defaultValues={defaultValues}
                                             causaliWF={lavoratoreLocale.causaliWF.filter(tutteLeRichiesteNonElaborate)}
                                             resetWarning={(warning) => {
                                                 updateDraft(draft => {
                                                     PresenzeGiornataPlusRecord.checkWarning(draft, warning);
                                                 })
                                             }}
                    />

                    <div className="flex flex-col">
                        <div className="flex flex-col">
                            <div className="mb-3 lg:pl-2 xl:pl-0 lg:w-8/12">
                                <ComboBoxTurno<PresenzeFormValues> label="Turno" inlineLabel={true}
                                                                   labelClassName="w-3/12 md:w-2/12 !text-[13px] items-center font-semibold text-elemPresenze content-center truncate" /* w-[50px] md:w-[90px] lg:w-[100px] xl:w-[110px] 2xl:w-[170px] */
                                                                   control={control} name="codTurno" defaultValue={null}
                                                                   data={turni}
                                                                   isLoading={false} valueAsObject={false}
                                                                   disabled={false} rules={{required: true}}
                                                                   onChange={cambioTurno}
                                                                   raggruppaOpts={true}
                                />
                            </div>

                            {/* *** PREVISTE *** */}
                            <TimbraturePLUS labelRowTimbrature="Previste" tipoTimbrature="previste" disabled={previsteLocked}>
                                <ButtonsTimbrature allineaCausali={() => allineaCausali(getValues())} locked={previsteLocked} tipoTimbrature="previste"
                                                   onClickLock={togglePrevisto}
                                                   onClickCopy={() => {
                                                       updateDraft(draft => {
                                                           draft.previsteLocked = false;
                                                           draft.previste = getValues('arrotondate')
                                                           PresenzeGiornataPlusRecord.resetAnomalia(draft);
                                                       })
                                                   }}
                                                   onClickClear={() => {
                                                       updateDraft(draft => {
                                                           draft.previsteLocked = false;
                                                           draft.previste = [];
                                                           initTimbrature([], draft.previste);
                                                           PresenzeGiornataPlusRecord.resetAnomalia(draft);
                                                       })
                                                   }}
                                />
                            </TimbraturePLUS>

                            {/* *** EFFETTIVE *** */}
                            {!riordinoEffettive && <>
                                <TimbraturePLUS tipoTimbrature="effettive" labelRowTimbrature="Effettive" showLabels={false}
                                                effettive={timbEffettive}
                                                onChangeOraInizio={onChangeOraInizio}
                                                onChangeOraFine={onChangeOraFine}
                                                effettiveOrig={timbEffettiveOrig}>
                                    <ButtonsTimbrature allineaCausali={() => allineaCausali(getValues())} tipoTimbrature="effettive"
                                                       locked={true}
                                                       handleEditEffettive={() => setRiordinoEffettive(true)}
                                                       isEffettiveOriginali={isEffettiveOriginali(defaultValues.effettive, defaultValues.effettiveOriginali, defaultValues.maskEffettive)}/>
                                </TimbraturePLUS>
                            </>}

                            {/* *** EFFETTIVE ORIGINALI *** */}
                            {riordinoEffettive && <>
                                <TimbraturePLUS tipoTimbrature="effettiveOriginali" labelRowTimbrature="Effettive originali" showLabels={false} disabled={!editEffettiveOriginali}
                                                effettive={timbEffettive}
                                                effettiveOrig={timbEffettiveOrig}>
                                    <ButtonsTimbrature allineaCausali={() => allineaCausali(getValues())} tipoTimbrature="effettiveOriginali"
                                                       locked={true}
                                                       editEffettive={riordinoEffettive}  /* specifica se strisciata in edit (riordino) */
                                                       confirmEditEffettive={confirmEditEffettive}
                                                       closeEditEffettive={cancelEditEffettive}/>
                                </TimbraturePLUS>
                            </>}

                            {/* *** ARROTONDATE *** */}
                            <TimbraturePLUS labelRowTimbrature="Arrotondate" tipoTimbrature="arrotondate" showLabels={false} disabled={arrotondateLocked}>
                                <ButtonsTimbrature allineaCausali={() => allineaCausali(getValues())} locked={arrotondateLocked} tipoTimbrature="arrotondate"
                                                   onClickLock={() => {
                                                       updateDraft(draft => {
                                                           draft.arrotondateLocked = !arrotondateLocked;
                                                           PresenzeGiornataPlusRecord.resetAnomalia(draft);
                                                       })
                                                   }}
                                                   onClickCopy={() => {
                                                       updateDraft(draft => {
                                                           draft.arrotondateLocked = false;
                                                           draft.arrotondate = getValues('previste')
                                                           PresenzeGiornataPlusRecord.resetAnomalia(draft);
                                                       })
                                                   }}
                                                   onClickClear={() => {
                                                       updateDraft(draft => {
                                                           draft.arrotondateLocked = false;
                                                           draft.arrotondate = [];
                                                           initTimbrature([], draft.arrotondate);
                                                           PresenzeGiornataPlusRecord.resetAnomalia(draft);
                                                       })
                                                   }}
                                />
                            </TimbraturePLUS>

                            {/* GIUSTIFICATIVI AD ORE */}
                            <div className="flex flex-col">
                                <EditGiustificativiHH lavoratore={lavoratoreLocale} day={defaultValues} giustificativi={listaGiustificativi}
                                                      gruppiEventi={gestione.cachePresenze?.gruppiEventi}
                                                      giuClassName="justify-center"
                                                      labelClassName="!font-semibold !text-elemPresenze !text-[13px]"
                                                      quantitaOrarieIn={'MINUTI'}
                                                      onChange={resetLocalAnomalia}
                                />
                            </div>

                            {/* GIUSTIFICATIVI A QUANTITA'/IMPORTO */}
                            <div className="flex flex-col">
                                <EditGiustificativiQI lavoratore={lavoratoreLocale} day={defaultValues} giustificativi={listaGiustificativi}
                                                      gruppiEventi={gestione.cachePresenze?.gruppiEventi}
                                                      giuClassName="justify-center"
                                                      labelClassName="!font-semibold !text-elemPresenze !text-[13px]"
                                    /*onChange={resetLocalAnomalia}*/
                                />
                            </div>

                            {/* RIEPILOGO ORE */}
                            {lavoratoreLocale.totaliMese && <div className="grid grid-cols-12 w-full min-h-[220px]">
                                <div className="col-span-3 content-center">
                                    <TabsRiepilogo activeTab={activeTab} setActiveTab={setActiveTab} showTabAccumuli={!isEmployee && showRiepilogoAccumuli}/>
                                </div>
                                <div className="col-span-6">
                                    {activeTab === 'day' && <RiepilogoOre totali={defaultValues.totali} riepilogoGiornaliero={true}
                                                                          captionClassName="text-[15px] text-elemPresenze"/>}
                                    {activeTab === 'week' && <RiepilogoOre totali={defaultValues.week?.totali || null}
                                                                           captionClassName="text-[15px] text-elemPresenze"/>}

                                    {activeTab === 'month' && <RiepilogoOre totali={lavoratoreLocale.totaliMese.totali}
                                                                            captionClassName="text-[15px] text-elemPresenze"
                                                                            showTogglePeriodo={true}
                                                                            soloMeseRif={soloMeseRif}
                                                                            setSoloMeseRif={setSoloMeseRif}
                                    />}

                                    {activeTab === 'accumuli' && <AccumuliDiControllo totali={lavoratoreLocale.totaliMese}/>}
                                </div>
                            </div>}
                        </div>

                        {/* BUTTONS PANEL GIORNATA */}
                        <div className="inline-flex items-center w-full p-0.5 mt-4">
                            <PulsantieraGiornataPlusSTD disabled={riordinoEffettive} dismissModal={onCancelEdit} calcola={calcola} compensa={compensa}/>
                        </div>

                        <OpzioniGiornata dtGiornata={dtGiornata} updateDefaultValues={updateDraft}/>

                        <LogGiornata day={defaultValues} lavoratore={lavoratoreLocale} updCycle={updCycle}/>
                    </div>
                </form>
            </FormProvider>
        </div>
    </ModalPanelForm>
};

export default PannelloGiornataPLUS;